/**
 * Product Card Initialization & Interaction
 * 
 * 1. IMAGE INITIALIZATION:
 *    Ensures the correct variant image is shown on initial load based on the URL variant parameter
 *    or the pre-selected variant from search results.
 * 
 * 2. SWATCH INTERACTION:
 *    Updates the product card URL immediately when a user selects a color swatch.
 *    (The theme's default behavior sometimes lags or doesn't update the URL on cards correctly)
 */

import { onDocumentReady } from '@theme/utilities';

function getHoverSizePanelState() {
  // @ts-ignore - custom global state for the size hover panel
  window.hoverSizePanelState = window.hoverSizePanelState || {
    closeTimeout: null,
    isPanelInteracting: false,
  };

  // @ts-ignore - custom global state for the size hover panel
  return window.hoverSizePanelState;
}

function clearHoverSizePanelCloseTimeout() {
  clearTimeout(getHoverSizePanelState().closeTimeout);
}

function getHoverWrapper(element) {
  return element instanceof Element ? element.closest('.product-card-hover-wrapper') : null;
}

function getHoverPanel(wrapper) {
  return wrapper instanceof HTMLElement ? wrapper.querySelector('.hover-size-panel') : null;
}

function resetHoverPanelSelection(panel) {
  if (!(panel instanceof HTMLElement)) return;

  panel.querySelectorAll('li.selected').forEach((item) => item.classList.remove('selected'));
  delete panel.dataset.selectedVariant;
}

function hoverPanelHasSelectedSize(panel) {
  return Boolean(panel?.dataset.selectedVariant || panel?.querySelector('li.selected'));
}

function parseVariantsFromWrapper(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return [];

  const jsonElement = wrapper.querySelector('.variants-data');
  if (!(jsonElement instanceof HTMLScriptElement)) return [];

  try {
    const parsed = JSON.parse(jsonElement.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function renderHoverSizeList(wrapper, colorValue) {
  if (!(wrapper instanceof HTMLElement)) return;

  const colorIndex = Number(wrapper.dataset.colorIndex);
  const sizeIndex = Number(wrapper.dataset.sizeIndex);
  if (Number.isNaN(sizeIndex)) return;

  const panel = getHoverPanel(wrapper);
  const list = wrapper.querySelector('.hover-size-list');
  if (!(panel instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

  const variants = parseVariantsFromWrapper(wrapper);
  const seen = new Set();
  const items = [];

  for (const variant of variants) {
    const variantColor = Number.isNaN(colorIndex) ? null : variant.options?.[colorIndex];
    if (colorValue && variantColor !== colorValue) continue;
    if (variant.available !== true) continue;

    const sizeValue = variant.options?.[sizeIndex];
    if (!sizeValue || seen.has(sizeValue)) continue;

    seen.add(sizeValue);
    items.push(
      `<li data-variant-id="${variant.id}" data-size="${String(sizeValue).replace(/"/g, '&quot;')}">${sizeValue}</li>`
    );
  }

  list.innerHTML = items.join('');
  resetHoverPanelSelection(panel);
}

function collectCartSectionIds() {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.add(item.dataset.sectionId);
    }
  });

  return Array.from(sectionIds);
}

async function refreshCartSections() {
  const sectionIds = collectCartSectionIds();
  if (!window.Theme?.routes?.cart_update_url || sectionIds.length === 0) {
    return { item_count: 1, sections: {} };
  }

  const response = await fetch(window.Theme.routes.cart_update_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  const responseText = await response.text();

  try {
    return JSON.parse(responseText);
  } catch (error) {
    return { item_count: 1, sections: {} };
  }
}

function dispatchProductCardCartUpdate(variantId, sourceElement, source, parsedResponse) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: {},
        sourceId: variantId,
        data: {
          didError: false,
          source,
          productId: sourceElement?.closest('.product-card-hover-wrapper')?.dataset.productId || null,
          itemCount: parsedResponse?.item_count || 1,
          sections: parsedResponse?.sections || {},
        },
      },
    })
  );
}

function openCartDrawer(delay = 100) {
  setTimeout(() => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    const drawerButton = document.querySelector(
      'cart-drawer-component > button[aria-label*="carrito"], cart-drawer-component > button[aria-label*="Abrir"]'
    );
    if (drawerButton instanceof HTMLElement) {
      drawerButton.click();
    }
  }, delay);
}

function ensureMobileSizeSheet() {
  if (document.getElementById('mobile-size-sheet-global')) return;
  if (!document.body) return;

  document.body.insertAdjacentHTML(
    'beforeend',
    `
      <div id="mobile-size-overlay-global" class="mobile-size-overlay" onclick="closeMobileSizeSheet()"></div>
      <div id="mobile-size-sheet-global" class="mobile-size-sheet">
        <div class="mobile-size-sheet-header">
          <h3 class="mobile-size-sheet-title"></h3>
          <button type="button" class="mobile-size-sheet-close" onclick="closeMobileSizeSheet()" aria-label="Cerrar">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <ul class="mobile-size-sheet-list"></ul>
        <div class="mobile-size-sheet-footer"></div>
      </div>
    `
  );
}

function getCurrentColorForWrapper(wrapper, variants) {
  if (!(wrapper instanceof HTMLElement)) return null;

  const colorIndex = Number(wrapper.dataset.colorIndex);
  const productCard = wrapper.closest('product-card');
  const swatchesRoot = productCard?.querySelector('swatches-variant-picker-component');
  const checkedSwatch = swatchesRoot?.querySelector('input[type="radio"][name$="-swatch"]:checked');

  if (checkedSwatch instanceof HTMLInputElement) {
    return checkedSwatch.getAttribute('aria-label') || checkedSwatch.value;
  }

    // FIX B4B (3): fallback a data-b4b-color si no hay swatch marcado
    const b4bLi = wrapper.closest('li[data-b4b-color]');
    if (b4bLi) {
      const b4bColor = b4bLi.getAttribute('data-b4b-color');
      if (b4bColor) return b4bColor;
    }

    if (!Number.isNaN(colorIndex)) {
      const firstAvailableVariant = variants.find((variant) => variant.available === true);
      return firstAvailableVariant?.options?.[colorIndex] || null;
    }

  return null;
}

function setupHoverSizePanelGlobals() {
  getHoverSizePanelState();

  window.openSizePanel = function(element) {
    clearHoverSizePanelCloseTimeout();

    const wrapper = getHoverWrapper(element);
    const panel = getHoverPanel(wrapper);
    if (panel instanceof HTMLElement) {
      panel.classList.add('active');
    }
  };

  window.handlePanelMouseLeave = function(panel) {
    if (!(panel instanceof HTMLElement) || hoverPanelHasSelectedSize(panel)) return;

    clearHoverSizePanelCloseTimeout();
    getHoverSizePanelState().closeTimeout = setTimeout(() => {
      panel.classList.remove('active');
    }, 150);
  };

  window.handleWrapperMouseLeave = function(wrapper) {
    const panel = getHoverPanel(wrapper);
    if (!(panel instanceof HTMLElement) || hoverPanelHasSelectedSize(panel)) return;

    clearHoverSizePanelCloseTimeout();
    getHoverSizePanelState().closeTimeout = setTimeout(() => {
      panel.classList.remove('active');
      resetHoverPanelSelection(panel);
    }, 150);
  };

  window.delayedCloseSizePanel = function() {};

  window.cancelClose = function() {
    clearHoverSizePanelCloseTimeout();
  };

  window.forceClose = function(wrapper) {
    clearHoverSizePanelCloseTimeout();

    const panel = getHoverPanel(wrapper);
    if (!(panel instanceof HTMLElement)) return;

    panel.classList.remove('active');
    resetHoverPanelSelection(panel);
  };

  window.closeMobileSizeSheet = function() {
    const sheet = document.getElementById('mobile-size-sheet-global');
    const overlay = document.getElementById('mobile-size-overlay-global');

    sheet?.classList.remove('active');

    setTimeout(() => {
      overlay?.classList.remove('active');
      document.body.style.overflow = '';
      // @ts-ignore - global pointer to the active wrapper used by the mobile sheet
      window.currentMobileSizeWrapper = null;
    }, 350);
  };

  window.openMobileSizeSheet = function(triggerButton) {
    const wrapper = getHoverWrapper(triggerButton);
    if (!(wrapper instanceof HTMLElement)) return;

    ensureMobileSizeSheet();

    // @ts-ignore - global pointer to the active wrapper used by the mobile sheet
    window.currentMobileSizeWrapper = wrapper;

    const variants = parseVariantsFromWrapper(wrapper);
    const currentColor = getCurrentColorForWrapper(wrapper, variants);
    const sizeIndex = Number(wrapper.dataset.sizeIndex);
    if (Number.isNaN(sizeIndex)) return;

    const seen = new Set();
    const sizeItems = [];

    for (const variant of variants) {
      const colorIndex = Number(wrapper.dataset.colorIndex);
      if (!Number.isNaN(colorIndex) && currentColor) {
        const variantColor = variant.options?.[colorIndex];
        if (variantColor !== currentColor) continue;
      }

      if (variant.available !== true) continue;

      const sizeValue = variant.options?.[sizeIndex];
      if (!sizeValue || seen.has(sizeValue)) continue;

      seen.add(sizeValue);
      sizeItems.push(
        `<li data-variant-id="${variant.id}" data-size="${String(sizeValue).replace(/"/g, '&quot;')}">${sizeValue}</li>`
      );
    }

    const sheet = document.getElementById('mobile-size-sheet-global');
    const overlay = document.getElementById('mobile-size-overlay-global');
    const titleElement = sheet?.querySelector('.mobile-size-sheet-title');
    const listElement = sheet?.querySelector('.mobile-size-sheet-list');

    if (!(titleElement instanceof HTMLElement) || !(listElement instanceof HTMLElement)) return;

    titleElement.textContent =
      wrapper.dataset.productTitle ||
      triggerButton?.getAttribute?.('aria-label') ||
      wrapper.closest('product-card')?.querySelector('p')?.textContent?.trim() ||
      'Seleccionar talla';

    listElement.innerHTML = sizeItems.length > 0
      ? sizeItems.join('')
      : '<li style="color:#999;pointer-events:none;">No hay tallas disponibles</li>';

    document.body.style.overflow = 'hidden';
    overlay?.classList.add('active');

    requestAnimationFrame(() => {
      sheet?.classList.add('active');
    });
  };

  window.addVariantToCart = async function(variantId, sourceElement, options = {}) {
    if (!variantId) return;

    const { source = 'product-card-hover', closeMobileSheet = false } = options;

    if (sourceElement instanceof HTMLElement) {
      sourceElement.classList.add('loading');
      if (sourceElement.tagName === 'BUTTON') {
        sourceElement.style.background = '#000';
        sourceElement.style.color = '#fff';
      }
    }

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!addResponse.ok) throw new Error('Failed to add to cart');

      const parsedResponse = await refreshCartSections();
      dispatchProductCardCartUpdate(variantId, sourceElement, source, parsedResponse);

      if (sourceElement instanceof HTMLElement) {
        sourceElement.classList.remove('loading');
        if (sourceElement.tagName === 'LI') {
          sourceElement.classList.add('added');
        }
      }

      if (closeMobileSheet) {
        window.closeMobileSizeSheet();
        openCartDrawer(400);
      } else {
        openCartDrawer(100);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);

      if (sourceElement instanceof HTMLElement) {
        sourceElement.classList.remove('loading');
        if (sourceElement.tagName === 'BUTTON') {
          sourceElement.style.background = '';
          sourceElement.style.color = '';
        } else {
          sourceElement.style.background = '#ffbaba';
          setTimeout(() => {
            if (sourceElement instanceof HTMLElement) {
              sourceElement.style.background = '';
            }
          }, 600);
        }
      }
    }
  };

  window.addSelectedVariantToCart = function(button) {
    const wrapper = getHoverWrapper(button);
    const panel = getHoverPanel(wrapper);
    const variantId = panel?.dataset.selectedVariant;

    if (!variantId) {
      button?.classList?.add('error');
      setTimeout(() => button?.classList?.remove('error'), 600);
      return;
    }

    window.addVariantToCart(variantId, button);
  };
}

function setupHoverSizePanelListeners() {
  // @ts-ignore - one-time global listener setup flag
  if (!window.__hoverSizePanelListenersSetup) {
    // @ts-ignore - one-time global listener setup flag
    window.__hoverSizePanelListenersSetup = true;

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('.hover-size-list li')) {
        event.preventDefault();
        event.stopPropagation();

        const panel = target.closest('.hover-size-panel');
        if (!(panel instanceof HTMLElement)) return;

        panel.querySelectorAll('li').forEach((item) => item.classList.remove('selected'));
        target.classList.add('selected');
        panel.dataset.selectedVariant = target.dataset.variantId || '';

        window.addVariantToCart(target.dataset.variantId, target);
        return;
      }

      if (target.matches('.mobile-size-sheet-list li') && target.style.pointerEvents !== 'none') {
        event.preventDefault();
        event.stopPropagation();

        const variantId = target.dataset.variantId;
        if (!variantId) return;

        target.classList.add('selected');
        window.addVariantToCart(variantId, target, {
          source: 'mobile-size-sheet',
          closeMobileSheet: true,
        });
      }
    }, true);

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.matches('swatches-variant-picker-component input[type="radio"][name$="-swatch"]')) return;

      const productCard = target.closest('product-card');
      const wrapper = productCard?.querySelector('.product-card-hover-wrapper');
      if (!(wrapper instanceof HTMLElement)) return;

      const colorValue = target.getAttribute('aria-label') || target.value;
      if (!colorValue) return;

      renderHoverSizeList(wrapper, colorValue);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const sheet = document.getElementById('mobile-size-sheet-global');
      if (sheet?.classList.contains('active')) {
        window.closeMobileSizeSheet();
      }
    });
  }
}

setupHoverSizePanelGlobals();
setupHoverSizePanelListeners();

/**
 * Initializes product cards to show the correct variant image and price.
 * FIX: Uses querySelector(':checked') instead of variantPicker.selectedOption
 * to ensure compatibility with cards where the custom element isn't fully upgraded yet.
 *
 * @param {NodeList|Array} [cards] - Optional specific cards to initialize.
 */
function initializeProductCardImages(cards) {
  const productCards = cards || document.querySelectorAll('product-card');

  productCards.forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    if (card.hasAttribute('data-image-initialized')) return;

    // FIX B4B (3): inicializar hover size list con el color correcto del card B4B
    const b4bLi = card.closest('li[data-b4b-color]');
    if (b4bLi) {
      const b4bColor = b4bLi.getAttribute('data-b4b-color');
      const sizeWrapper = b4bLi.querySelector('.product-card-hover-wrapper');
      if (b4bColor && sizeWrapper) renderHoverSizeList(sizeWrapper, b4bColor);
    }

    const variantPicker = card.querySelector('swatches-variant-picker-component');
    if (!variantPicker) return;

    // FIX: usar querySelector en lugar de variantPicker.selectedOption
    const selectedOption = variantPicker.querySelector('input[type="radio"]:checked');
    if (!selectedOption) return;

    const selectedMediaId = selectedOption.dataset.optionMediaId;
    const variantId = selectedOption.dataset.firstAvailableOrFirstVariantId;
    const productUrl = variantPicker.dataset.productUrl?.split('?')[0];

    // Fix imagen
    if (selectedMediaId) {
      const slideshow = card.querySelector('slideshow-component');
      if (slideshow) {
        const slides = slideshow.querySelectorAll('slideshow-slide');
        slides.forEach((slide) => {
          const isVariantImage = slide.hasAttribute('variant-image');
          const slideId = slide.getAttribute('slide-id');
          if (isVariantImage && slideId !== selectedMediaId) {
            slide.hidden = true;
          } else if (isVariantImage && slideId === selectedMediaId) {
            slide.hidden = false;
          }
        });

        setTimeout(() => {
          if (typeof slideshow.select === 'function') {
            slideshow.select({ id: selectedMediaId }, undefined, { animate: false });
          }
        }, 300);
      }
    }

    // Fix precio para cards secundarios
    if (variantId && productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      fetch(url.href)
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newPrice = doc.querySelector("product-price [ref='priceContainer']");
          const prices = card.querySelectorAll("product-price [ref='priceContainer']");
          const priceContainer = prices[1] || prices[0];
          if (newPrice && priceContainer && typeof morph === 'function') {
            morph(priceContainer, newPrice);
          }
        })
        .catch(() => {});
    }

    card.setAttribute('data-image-initialized', 'true');
  });
}

/**
 * Sets up a global event listener for variant changes.
 * Updates product card link URL and image when a swatch is selected.
 */
function setupGlobalVariantListener() {
  // @ts-ignore - custom property on window
  if (window.__variantListenerSetup) return;
  // @ts-ignore - custom property on window
  window.__variantListenerSetup = true;

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.name?.includes('-swatch')) return;

    const productCard = target.closest('product-card');
    if (!productCard) return;

    const variantId = target.dataset.variantId;
    if (!variantId) return;

    // Actualizar URL del link
    const productCardLink = productCard.querySelector('a[ref="productCardLink"]');
    if (productCardLink instanceof HTMLAnchorElement) {
      const currentUrl = new URL(productCardLink.href);
      currentUrl.searchParams.set('variant', variantId);
      productCardLink.href = currentUrl.toString();
    }

    // FIX: Actualizar imagen al cambiar swatch
    const mediaId = target.dataset.optionMediaId;
    if (mediaId) {
      const slideshow = productCard.querySelector('slideshow-component');
      if (slideshow && typeof slideshow.select === 'function') {
        slideshow.select({ id: mediaId }, undefined, { animate: true });
      }
    }
  }, true);
}

// Run on document ready
onDocumentReady(() => {
  // 1. Set up interaction listeners
  setupGlobalVariantListener();

  // 2. Initialize images (one-time pass)
  setTimeout(initializeProductCardImages, 100);

  // 3. Watch for dynamically loaded products (pagination, infinite scroll)
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let reinitTimer;
  /** @type {Set<HTMLElement>} */
  const cardsToInitialize = new Set();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) {
          if (node.tagName === 'PRODUCT-CARD') {
            cardsToInitialize.add(node);
          } else {
            const nestedCards = node.querySelectorAll('product-card');
            nestedCards.forEach(card => {
              if (card instanceof HTMLElement) cardsToInitialize.add(card);
            });
          }
        }
      }
    }

    if (cardsToInitialize.size > 0) {
      clearTimeout(reinitTimer);
      reinitTimer = setTimeout(() => {
        const batch = Array.from(cardsToInitialize);
        cardsToInitialize.clear();
        initializeProductCardImages(batch);
      }, 200);
    }
  });

  const productGrid = document.querySelector('[ref="grid"]');
  if (productGrid) {
    observer.observe(productGrid, {
      childList: true,
      subtree: false
    });
  } else {
    observer.observe(document.body, {
      childList: true,
      subtree: false
    });
  }
});
